import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/solid"
import React from "react"

import { ToastProvider } from "../../contexts/ToastContext"
import GraphQLProvider from "../../providers/GraphQLProvider"

import AddSectionScreen from "./AddSectionScreen"
import EditSectionScreen from "./EditSectionScreen"
import EditSitewideStyleScreen from "./EditSitewideStyleScreen"
import PagesAccordion from "./PagesAccordion"
import SitewideStylesForm from "./sitewide-styles"
import { useWebsiteBuilderV2, WebsiteBuilderV2Provider } from "./WebsiteBuilderV2Context"
import WebsitePreviewViewV2 from "./WebsitePreviewViewV2"

import "../../components/shared/wysiwyg.sass"

export const DEFAULT_HIGHLIGHT_CLASSES = "rounded border-2 border-teal shadow-diffuse bg-teal/10"

const AnimatedSection = ({ section, isActive, toggleSection, icon, children }) => (
  <div className="overflow-hidden">
    <button
      className="inline-flex h-14 w-full items-center justify-between border-b border-gray bg-gray-ultralight px-8 py-4 transition-colors"
      onClick={() => toggleSection(section)}>
      <span className="flex items-center gap-3">
        {icon}
        <span className="text-lg font-bold leading-snug">{section}</span>
      </span>
      {isActive ? <ChevronUpIcon className="h-5 w-5" /> : <ChevronDownIcon className="h-5 w-5" />}
    </button>
    <div
      className={`transition-all duration-300 ease-in-out ${
        isActive ? "max-h-[5000px] opacity-100" : "max-h-0 opacity-0"
      }`}>
      <div>{children}</div>
    </div>
  </div>
)

const WebsiteBuilderV2 = () => {
  const {
    setIsPreviewing,
    setOpenField,
    setHighlightedField,
    activeAccordionSection,
    setActiveAccordionSection,
    showAddSectionScreen,
    setShowAddSectionScreen,
    showEditSectionScreen,
    setShowEditSectionScreen,
    showEditSitewideStyleScreen,
    setShowEditSitewideStyleScreen,
    availableLayouts,
    handleCreateMultipleSections
  } = useWebsiteBuilderV2()

  const toggleSection = (section) => {
    if (activeAccordionSection === section) {
      // If the section is already active, close it
      setActiveAccordionSection(null)
    } else {
      // If opening a new section, close all pages and open this section
      setActiveAccordionSection(section)
    }
    setIsPreviewing(false)
    setOpenField(null)
    setHighlightedField(null)
  }

  const handlePageToggle = (pageSlug) => {
    console.log("pageSlug", pageSlug) // eslint-disable-line no-console
    // This function will be called when a page is toggled
    // We can use this to track which page is currently open
  }

  const handleCloseAddSectionScreen = () => {
    setShowAddSectionScreen(false)
  }

  const handleAddSections = async (selectedSections) => {
    try {
      await handleCreateMultipleSections(selectedSections)
    } catch (error) {
      // Error handling is done in the context
    }
  }

  // If showing add section screen, render that instead of accordion
  if (showAddSectionScreen) {
    return (
      <AddSectionScreen
        availableLayouts={availableLayouts}
        onClose={handleCloseAddSectionScreen}
        onAddSections={handleAddSections}
      />
    )
  }

  // If showing edit section screen, render that instead of accordion
  if (showEditSectionScreen) {
    return <EditSectionScreen onClose={() => setShowEditSectionScreen(false)} />
  }

  // If showing edit sitewide style screen, render that instead of accordion
  if (showEditSitewideStyleScreen) {
    return <EditSitewideStyleScreen onClose={() => setShowEditSitewideStyleScreen(false)} />
  }

  return (
    <div>
      <AnimatedSection
        section="Sitewide Styles"
        isActive={activeAccordionSection === "Sitewide Styles"}
        toggleSection={toggleSection}>
        <SitewideStylesForm />
      </AnimatedSection>

      <PagesAccordion onPageToggle={handlePageToggle} />
    </div>
  )
}

export default function WebsiteBuilderV2Page(props) {
  return (
    <ToastProvider>
      <GraphQLProvider>
        <WebsiteBuilderV2Provider {...props}>
          <WebsitePreviewViewV2>
            <WebsiteBuilderV2 />
          </WebsitePreviewViewV2>
        </WebsiteBuilderV2Provider>
      </GraphQLProvider>
    </ToastProvider>
  )
}
